import * as WebBrowser from 'expo-web-browser';
import React, { useEffect, useMemo, useState } from 'react';
import { Platform, View } from 'react-native';
import type { Country } from 'react-native-country-picker-modal';
import { KeyboardAwareScrollView } from 'react-native-keyboard-aware-scroll-view';
import Toast from 'react-native-toast-message';
import { createStyleSheet, useStyles } from 'react-native-unistyles';

import { type AuthTokenResponse } from '@/api/customers/use-create-customer';
import { useCustomer } from '@/api/customers/use-customer';
import { useLoginCustomerApple } from '@/api/customers/use-login-customer-apple';
import { useLoginCustomerGoogle } from '@/api/customers/use-login-customer-google';
import type { StartOtpResponse } from '@/api/customers/use-start-otp';
import { useStartOtp } from '@/api/customers/use-start-otp';
import { AppleLogin } from '@/components/apple-login/apple-login';
import ConfirmOtp from '@/components/confirm-otp';
import { GoogleLogin } from '@/components/google-login';
import { InputPhone } from '@/components/input-phone';
import Navbar from '@/components/login-modal/navbar';
import SigninEmailForm from '@/components/signin/email-form';
import { signIn, useAuth, validatePhoneNumber } from '@/core';
import mixpanelService from '@/core/clients/mixpanel';
import { useCurrentOrganization } from '@/core/organization';
import useSettingsStore from '@/stores/use-settings-store';
import { Button, showErrorMessage, Text } from '@/ui';
import { Icon } from '@/ui/icon';
import { Eon } from '@/ui/icons/eon';
import { Mail } from '@/ui/icons/mail';
import { Loader } from '@/ui/loader';

import type { PhoneCountry } from '../input-phone/types';
import { useModalStore } from '../login-modal/store';
import { NavigationButtonTypes } from '../navigation-button';
import FinishSignup from '../signup/finish-signup';

type InputPhoneProps = {
  phone: string;
  country: Country;
};

interface SigninProps {
  onBack: () => void;
  onDone: () => void;
}

export default function Signin({ onBack, onDone }: SigninProps) {
  const { styles, theme } = useStyles(stylesheet);
  const [phone, setPhone] = useState('');
  const [country, setCountry] = useState<PhoneCountry>();
  const [showFinishSignup, setShowFinishSignup] = useState(false);
  const [showConfirmOtp, setShowConfirmOtp] = useState(false);
  const [showSigninEmailForm, setShowSigninEmailForm] = useState(false);
  const setLoginModalMode = useModalStore((state) => state.setLoginModalMode);
  const setFinishingSignup = useModalStore((state) => state.setFinishingSignup);
  const refCode = useSettingsStore((state) => state.refCode);
  const promoCode = useSettingsStore((state) => state.promoCode);
  const { slug: organizationSlug } = useCurrentOrganization();

  const status = useAuth.use.status();
  const { data: customer, refetch: refetchCustomer } = useCustomer();

  useEffect(() => {
    mixpanelService.track('Page Viewed', {
      'Page Name': 'Sign In',
    });
  }, []);

  useEffect(() => {
    if (status === 'signIn' && customer && !customer.setupComplete) {
      setFinishingSignup(true);
      setShowFinishSignup(true);
    }
  }, [status, customer, setFinishingSignup]);

  const { mutate: loginCustomerGoogle, isPending: isLoggingInCustomerGoogle } =
    useLoginCustomerGoogle();

  const { mutate: loginCustomerApple, isPending: isLoggingInCustomerApple } =
    useLoginCustomerApple();

  const { mutate: startOtp, isPending: isStartingOtp } = useStartOtp();

  const isValidPhoneNumber = useMemo(() => {
    if (!phone) return false;

    return validatePhoneNumber(country, phone);
  }, [country, phone]);

  const handlePhoneSubmit = () => {
    startOtp(
      {
        phone: `+${country?.callingCode}${phone}`,
      },
      {
        onSuccess: (_: StartOtpResponse) => {
          setShowConfirmOtp(true);
          setPhone(`+${country?.callingCode}${phone}`);
        },
        onError: (e) => {
          showErrorMessage(e.message);
        },
      }
    );
  };

  const handleGoogleLogin = (credential: string) => {
    loginCustomerGoogle(
      {
        googleCredential: credential,
        referralCode: refCode,
        promoCode: promoCode,
      },
      {
        onSuccess: (respData: AuthTokenResponse) => {
          mixpanelService.track('Sign In Successful');

          Toast.show({
            text1: 'Signed in successfully',
            type: 'success',
          });

          if (respData.data.setupComplete) {
            onDone();
          } else {
            setFinishingSignup(true);
            setShowFinishSignup(true);
          }

          signIn(respData.data.token);
        },
        onError: (e) => {
          mixpanelService.track('Sign In Failed');

          showErrorMessage(e.message);
        },
      }
    );
  };

  const handleAppleLogin = (
    userId: string,
    identityToken: string,
    fullName: string
  ) => {
    loginCustomerApple(
      {
        userId,
        identityToken,
        fullName,
        referralCode: refCode,
        promoCode: promoCode,
      },
      {
        onSuccess: (respData: AuthTokenResponse) => {
          mixpanelService.track('Sign In Successful');
          Toast.show({
            text1: 'Signed in successfully',
            type: 'success',
          });
          signIn(respData.data.token);

          if (respData.data.setupComplete) {
            onDone();
          } else {
            setShowFinishSignup(true);
            setFinishingSignup(true);
          }
        },
        onError: (e) => {
          mixpanelService.track('Sign In Failed');

          console.log('e', e);
          showErrorMessage(e.message);
        },
      }
    );
  };

  const onBackPress = () => {
    if (showConfirmOtp) {
      setShowConfirmOtp(false);
      return;
    }

    if (showSigninEmailForm) {
      setShowSigninEmailForm(false);
      return;
    }

    onBack();
  };

  const onFinishSignupDone = (phone: string) => {
    setShowFinishSignup(false);
    setShowConfirmOtp(true);
    setPhone(phone);
  };

  if (isLoggingInCustomerGoogle || isStartingOtp) {
    return (
      <View
        style={{
          width: '100%',
          height: '100%',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: theme.colors.background_2,
        }}
      >
        <Loader style={{ width: 150, height: 150 }} />
      </View>
    );
  }

  const renderBody = () => {
    if (showFinishSignup) {
      return (
        <FinishSignup
          onBack={() => {
            setShowFinishSignup(false);
          }}
          onDone={onFinishSignupDone}
        />
      );
    }

    if (showConfirmOtp) {
      return (
        <ConfirmOtp
          phone={phone}
          onBack={() => setShowConfirmOtp(false)}
          onDone={(setupComplete: boolean) => {
            setShowConfirmOtp(false);

            if (setupComplete) {
              refetchCustomer();
              onDone();
            } else {
              setShowFinishSignup(true);
              setFinishingSignup(true);
            }
          }}
        />
      );
    }

    if (showSigninEmailForm) {
      return (
        <SigninEmailForm
          onBack={() => setShowSigninEmailForm(false)}
          onDone={(setupComplete: boolean) => {
            setShowSigninEmailForm(false);

            if (setupComplete) {
              onDone();
            } else {
              setShowFinishSignup(true);
              setFinishingSignup(true);
            }
          }}
        />
      );
    }

    return (
      <View style={{ flexGrow: 1 }}>
        <Navbar
          onPress={onBackPress}
          type={
            Platform.OS === 'web'
              ? NavigationButtonTypes.cancel
              : NavigationButtonTypes.back_with_text
          }
        />

        <View style={styles.container}>
          <KeyboardAwareScrollView
            style={{ backgroundColor: theme.colors.background_2 }}
          >
            <View style={styles.bottomContainer}>
              <View style={styles.topContainer}>
                <View style={styles.titleContainer}>
                  {Platform.OS !== 'web' && <Eon width="48" height="18" />}

                  <Text tx="signin.subtitle" type="title1" />

                  <Text
                    tx="signin.body"
                    type="bodyRegular"
                    style={styles.body}
                  />
                </View>

                <InputPhone
                  onChangeText={(text: string) => {
                    setPhone(text);
                  }}
                  onCountryChange={(c: PhoneCountry) => {
                    setCountry(c);
                  }}
                  style={{ width: '100%' }}
                />

                <Button
                  tx="signin.continue"
                  type="primary"
                  onPress={() => {
                    mixpanelService.track('Button Clicked', {
                      'Button Name': 'Sign In Phone',
                    });

                    handlePhoneSubmit();
                  }}
                  disabled={!isValidPhoneNumber}
                  style={{ width: '100%' }}
                />
              </View>

              <View style={{ alignItems: 'center' }}>
                <Text tx="signin.or" type="bodyRegular" style={styles.body} />
              </View>

              <View style={{ gap: 16 }}>
                {!organizationSlug ? (
                  <>
                    <GoogleLogin
                      onSuccess={handleGoogleLogin}
                      tx="signin.googleButton"
                      onPress={() => {
                        mixpanelService.track('Button Clicked', {
                          'Button Name': 'Sign In Google',
                        });
                      }}
                    />
                    <AppleLogin
                      tx="signin.appleButton"
                      onSuccess={handleAppleLogin}
                      onPress={() => {
                        mixpanelService.track('Button Clicked', {
                          'Button Name': 'Sign In Apple',
                        });
                      }}
                    />
                  </>
                ) : null}
                <Button
                  leftIcon={
                    <Icon
                      type="naked"
                      icon={<Mail />}
                      stroke={theme.colors.icon.nakedColor}
                      fillColor={theme.colors.icon.nakedColor}
                      width={20}
                      height={20}
                    />
                  }
                  tx="signin.emailButton"
                  type="secondary"
                  onPress={() => {
                    mixpanelService.track('Button Clicked', {
                      'Button Name': 'Sign In Email Form',
                    });

                    setShowSigninEmailForm(true);
                  }}
                  style={{ width: '100%' }}
                />
                <Button
                  tx="signin.noAccount"
                  type="ghost"
                  onPress={() => {
                    setLoginModalMode('signup');
                  }}
                />
              </View>

              <View
                style={{
                  flexDirection: 'row',
                  flexWrap: 'wrap',
                  gap: 4,
                  justifyContent: 'center',
                }}
              >
                <Text type="caption1">
                  By clicking continue, you agree to our
                </Text>
                <Text
                  type="caption1"
                  style={{
                    textDecorationLine: 'underline',
                  }}
                  onPress={async () => {
                    if (Platform.OS === 'web') {
                      window.open(
                        'https://www.eonrides.com/legal/rental-terms',
                        '_blank'
                      );
                    } else {
                      await WebBrowser.openBrowserAsync(
                        'https://www.eonrides.com/legal/rental-terms'
                      );
                    }
                  }}
                >
                  Terms of Service
                </Text>
                <Text type="caption1"> and</Text>
                <Text
                  type="caption1"
                  style={{
                    textDecorationLine: 'underline',
                  }}
                  onPress={async () => {
                    if (Platform.OS === 'web') {
                      window.open(
                        'https://www.eonrides.com/legal/terms-privacy-policy',
                        '_blank'
                      );
                    } else {
                      await WebBrowser.openBrowserAsync(
                        'https://www.eonrides.com/legal/terms-privacy-policy'
                      );
                    }
                  }}
                >
                  Privacy Policy
                </Text>
              </View>
            </View>
          </KeyboardAwareScrollView>
        </View>
      </View>
    );
  };

  return <View style={{ width: '100%' }}>{renderBody()}</View>;
}

const stylesheet = createStyleSheet((theme) => ({
  container: {
    flexGrow: 1,
    paddingLeft: 24,
    paddingRight: 24,
    paddingBottom: 24,
    gap: 16,
    backgroundColor: theme.colors.background_2,
    borderRadius: 16,
    borderTopStartRadius: 0,
    borderTopEndRadius: 0,
  },
  bottomContainer: {
    flex: 1,
    gap: 16,
  },
  topContainer: {
    gap: 16,
  },
  titleContainer: {
    gap: 16,
  },
  body: {
    color: theme.colors.text.secondary,
  },
}));
